<template>
  <ten-popup
    ref="popup"
    trigger-type="manual"
    :direction="direction"
    :destroy="false"
    :show="popupShow"
    :popup-class="_popupClass"
    @change="onPopupChange"
  >
    <template v-slot:trigger>
      <ten-input-select
        ref="input"
        :multiple="multiple"
        :class="_inputClass"
        :placeholder="placeholder"
        :size="size"
        :is-filtering-mode="_isFilteringMode"
        :disabled="disabled"
        :readonly="_readonly"
        :value="dataValue"
        :opened="popupShow"
        :get-input="getInput"
        :get-value-key="path => path"
        :get-value-text="path => getInputSelectText(path)"
        :clearable="clearable"
        @tag-delete="onTagDelete"
        @text="onFilter"
        @click.stop="onTriggerClick"
        @focus="onInputFocus"
        @blur="onInputBlur"
        @clear="onClear"
      >
        <template v-slot:suffixIcon>
          <ten-icon
            :icon="suffixIcon"
            @click.stop="onSuffixIconClick"
          />
        </template>
      </ten-input-select>
    </template>

    <template v-slot:default>
      <div
        class="ten-cascader__popup-content"
        @click="onPopupClick"
      >
        <ten-cascader-panel
          ref="cascaderPanel"
          v-bind="$attrs"
          :options="options"
          :on-options-update="onOptionUpdate"
          :options-title="optionsTitle"
          :value="multiple ? dataValue : dataValue[0]"
          :multiple="multiple"
          :independent="independent"
          :option-value="optionValue"
          :option-label="optionLabel"
          :option-children="optionChildren"
          :render-option="renderOption"
          :load-children="loadChildren"
          :expand-trigger="expandTrigger"
          :filter-text="filterValue"
          :filter-option="filterOption"
          :only-leaf-selectable="onlyLeafSelectable"
          @change="onChange"
        >
          <template v-slot:option="{ option }">
            <slot name="option" v-bind="{ option }">
              {{ option[optionLabel] }}
            </slot>
          </template>
        </ten-cascader-panel>
      </div>
    </template>
  </ten-popup>
</template>
<script>
import isEqual from 'lodash/isEqual';
import TenInputSelect from '../input/input-select';
import TenIcon from '../icon';
import TenCascaderPanel from '../cascader-panel';
import { isEmpty } from '../scripts/utils/helper';

export default {
  name: 'TenCascader',
  components: {
    TenInputSelect,
    TenIcon,
    TenCascaderPanel,
  },
  inject: {
    formItem: {
      from: 'tenFormItem',
      default: null,
    },
  },
  provide() {
    return {
      tenCascader: this,
      tenFormItem: undefined,
    };
  },
  props: {
    /**
     * v-model 当前值，对应事件 change
     */
    value: [String, Number, Array, Object],
    /**
     * 指定弹出方向
     * @defaultValue bottom left
     */
    direction: {
      type: String,
      default: 'bottom left',
    },
    /**
     * @member value | data
     */
    valueType: {
      type: String,
      default: 'value',
    },
    /**
     * 非受控时，可传入默认值
     */
    defaultValue: [String, Number, Array, Object],
    /**
     * 自定义选中值的显示，参数为选中值对应的 option 数组
     */
    customDisplayValue: Function,
    /**
     * 输入提示
     */
    placeholder: {
      type: String,
      default: '请选择',
    },
    /**
     * 大小
     * @defaultValue default
     * @member default|small
     */
    size: {
      type: String,
      default: 'default',
    },
    /**
     * 后缀图标
     * @defaultValue carret_down
     */
    suffixIcon: {
      type: String,
      default: 'carret_down',
    },
    /**
     * 单选模式，是否可以清空
     */
    clearable: Boolean,
    /**
     * 禁用
     */
    disabled: Boolean,
    /**
     * 只读
     */
    readonly: Boolean,
    /**
     * 选中值分割符
     */
    separator: {
      type: String,
      default: '/',
    },
    /**
     * 选项数据源
     */
    options: Array,
    /**
     * 选项 title，自定义每个选项列表的标题
     */
    optionsTitle: [Array, Function],
    /**
     * 是否多选
     */
    multiple: Boolean,
    /**
     * 每个选项都独立可选，而非只有叶子节点可选
     */
    independent: Boolean,
    /**
     * 指定选项的 value 在 data 中的键值
     */
    optionValue: {
      type: [String, Function],
      default: 'value',
    },
    /**
     * 指定选项的 label 在 data 中的键值
     */
    optionLabel: {
      type: [String, Function],
      default: 'label',
    },
    /**
     * 指定选项的 children 在 data 中的键值
     */
    optionChildren: {
      type: [String, Function],
      default: 'children',
    },
    /**
     * 自定义渲染 option 函数，不适用于筛选节点
     */
    renderOption: Function,
    /**
     * 自定义加载子节点方法，需设置需要动态加载的节点 children 为 true
     */
    loadChildren: Function,
    /**
     * 是否根据选项自动加载子节点（比如初始化 value 有值的时候）
     */
    autoLoadChildren: {
      type: Boolean,
      default: true,
    },
    /**
     * 子菜单的展开方式
     * @member click | hover
     */
    expandTrigger: {
      type: String,
      default: 'click',
    },
    /**
     * 筛选功能
     * 设置为 true 则根据 optionLabel 的值自动筛选
     * 设置为 false 不作过滤操作
     * 可以是函数，自定义筛选规则
     */
    filterOption: [Boolean, Function],
    /**
     * 仅子节点可选（一般在 multiple 模式下使用）
     */
    onlyLeafSelectable: Boolean,
    /**
     * 结果为空时提示文字，支持同名slot
     */
    emptyTip: {
      type: String,
      default: '暂无数据',
    },
  },
  data() {
    return {
      popupShow: false,
      filterValue: '',
      dataValue: undefined,
      dataPaths: [],
    };
  },
  computed: {
    _inputClass() {
      return [
        'ten-cascader-input',
        {
          'ten-cascader-input--popupshow': this.popupShow,
        },
      ];
    },
    _popupClass() {
      return [
        'ten-cascader__popup',
        {
          'ten-cascader__popup--loading': this.loading,
        },
      ];
    },
    _readonly() {
      return this.readonly || !(this._isFilteringMode && this.popupShow);
    },
    _isFilteringMode() {
      return Boolean(!!this.filterOption || this.$listeners.filter);
    },
  },
  watch: {
    value: {
      handler(v) {
        this.dataValue = this.transformValue(v);
      },
      immediate: true,
    },
  },
  created() {
    this.disableEmit = false;
  },
  methods: {
    transformValue(v) {
      let tValue;
      if (!this.multiple) {
        tValue = !isEmpty(v) ? [v] : [];
      } else {
        tValue = Array.isArray(v) ? v : [];
      }
      tValue = tValue.filter(item => Array.isArray(item));
      this.dataPaths = [];
      if (this.valueType === 'data') {
        tValue = tValue.filter(dataPath => dataPath.every(data => typeof data === 'object' && typeof data[this.optionValue] !== 'undefined')).map((dataPath) => {
          this.dataPaths.push(dataPath);
          return dataPath.map(data => data[this.optionValue]);
        });
      }
      return tValue;
    },
    reverseValue(v) {
      let reversedValue;
      if (this.valueType === 'data') {
        reversedValue = v
          .map(path => this.getDataPathByPath(path))
          .filter(item => item);
      } else {
        reversedValue = v;
      }
      if (!this.multiple) {
        reversedValue = reversedValue.length > 0 ? reversedValue[0] : null;
      }
      return reversedValue;
    },
    getDataPathByPath(path) {
      const dataPath = [];
      let children = this.options;

      for (let i = 0; i < path.length && Array.isArray(children); i++) {
        const key = path[i];
        const foundData = children.find(child => child[this.optionValue] === key);
        if (foundData) {
          dataPath.push(foundData);
          children = foundData[this.optionChildren];
        }
      }

      if (dataPath.length === path.length) {
        return dataPath;
      }

      const foundDataPath = this.dataPaths.find(dPath => isEqual(
        path,
        dPath.map(data => data[this.optionValue])
      ));

      return foundDataPath;
    },
    getInputSelectText(path) {
      const dataPath = this.getDataPathByPath(path);
      if (!dataPath) {
        return '';
      }
      if (this.customDisplayValue) {
        return this.customDisplayValue(dataPath);
      }
      return dataPath.map(data => data[this.optionLabel]).join(this.separator);
    },
    getInput(el) {
      this.refInput = el;
    },
    hidePopup() {
      this.popupShow = false;
      this.filterValue = '';
      /**
       * 失焦
       *
       * @event blur
       */
      this.$emit('blur');
      this.formItem && this.formItem.$emit('form.blur', this.value);
      this.$refs.cascaderPanel.blur();
    },
    trigger() {
      this.focus();
      setTimeout(this.onTriggerClick);
    },
    focus() {
      this.disableEmit = true;
      setTimeout(() => {
        this.disableEmit = false;
      }, 10);
      this.refInput && this.refInput.focus();
    },
    blur() {
      this.refInput && this.refInput.blur();
      this.hidePopup();
    },
    updateValue(unreversedValue) {
      const reversedValue = this.reverseValue(unreversedValue);
      /**
       * value 值改变事件
       *
       * @event change
       * @param value
       */
      this.$emit('change', reversedValue);
      this.$nextTick(() => {
        this.formItem && this.formItem.$emit('form.change');
      });
    },
    popupResize() {
      this.$refs.popup && this.$refs.popup.resize();
    },
    onOptionUpdate() {
      this.dataValue = [...this.dataValue];
    },
    onPopupClick() {
      this.popupResize();
      this.focus();
    },
    onChange(value) {
      if (this._isFilteringMode) {
        if (!this.multiple) {
          setTimeout(() => {
            this.filterValue = '';
          }, 300);
        } else {
          setTimeout(() => {
            this.filterValue = '';
            this.$refs.input && this.$refs.input.clearText();
          }, 0);
        }
      }
      if (!this.multiple) {
        this.popupShow = false;
      }
      this.updateValue(this.multiple ? value : [value]);
      this.focus();
    },
    onTagDelete(tagToDelete, afterDeleteTags) {
      const value = afterDeleteTags;

      this.focus();
      this.updateValue(value);
    },
    onFilter(text) {
      this.filterValue = text.trim();
      /**
       * @ignore
       * 正在输入筛选值
       */
      this.$emit('filter', text);
    },
    onTriggerClick() {
      if (
        this.readonly
        || this.disabled
        || (this._isFilteringMode && this.popupShow)
      ) {
        return;
      }
      this.popupShow = !this.popupShow;
      if (this.popupShow) {
        setTimeout(() => {
          this.$refs.cascaderPanel.focus();
        }, 300);
      }
    },
    onSuffixIconClick() {
      if (this.readonly || this.disabled) return;
      this.popupShow = !this.popupShow;
    },
    onClear() {
      this.updateValue([]);
    },
    onInputFocus(e) {
      if (!this.disableEmit) {
        /**
         * 聚焦事件
         *
         * @event focus
         * @param {Object} e
         */
        this.$emit('focus', e);
      }
    },
    onInputBlur(e) {
      /**
       * 失焦事件
       *
       * @event blur
       * @param {Object} e
       */
      this.$emit('blur', e);
      this.formItem && this.formItem.$emit('form.blur');
      this.hidePopup();
    },
    onPopupChange() {
      this.hidePopup();
    },
    renderEmptyTip() {
      const parentSlots = this.$parent.$slots;
      const defaultSlot = <span slot="emptyTip">{this.emptyTip}</span>;
      return parentSlots.emptyTip ? parentSlots.emptyTip[0] : defaultSlot;
    },
  },
};
</script>
